import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

import classes from "./styles.module.scss";

import { convertToTwoDigits } from "@utils";
import { Typography } from "@components/atoms/typography";
import { useAppointmentTable } from "@api/appointment";
import { Icon } from "@components/atoms/icons";
import "ldrs/ping";
import { useThemeVariable } from "@/theme";
import { queryClient } from "@/api";
import type { IAppointmentTimerProps } from "@components/organisms/appointmentCard/components/appointmentTimer/types";

export const AppointmentTimer = memo(function AppointmentTimer({
  startDate,
}: IAppointmentTimerProps) {
  const { t } = useTranslation("common");

  const { color } = useThemeVariable("--theme-danger-main");

  const calculateTimeLeft = useCallback(() => {
    const exactDifference = startDate ? Date.now() - +new Date(startDate) : 0;

    const difference = Math.abs(exactDifference);

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / (1000 * 60)) % 60),
      seconds: Math.floor((difference / 1000) % 60),
      isOnline: exactDifference > 0,
    };
  }, [startDate]);

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setTimeout(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearTimeout(timer);
  });

  const formatTimeLeft = useCallback(() => {
    return {
      time:
        Object.keys(timeLeft).length > 0
          ? `${convertToTwoDigits(timeLeft.days)}:${convertToTwoDigits(
              timeLeft.hours,
            )}:${convertToTwoDigits(timeLeft.minutes)}:${convertToTwoDigits(
              timeLeft.seconds,
            )}`
          : "- - -",
      isOnline: timeLeft.isOnline || false,
    };
  }, [timeLeft]);

  useEffect(() => {
    // eslint-disable-next-line no-undef
    let timeout: NodeJS.Timeout;

    if (startDate) {
      const ms = startDate.getTime();

      if (Date.now() < ms && ms - Date.now() < 24 * 60 * 60 * 1000) {
        timeout = setTimeout(() => {
          queryClient.invalidateQueries(useAppointmentTable.getKey());
        }, ms - Date.now());
      }
    }
    return () => clearTimeout(timeout);
  }, [startDate]);

  const { time, isOnline } = useMemo(() => {
    return formatTimeLeft();
  }, [formatTimeLeft]);

  return (
    <div className={clsx(classes.timer)}>
      <Typography variant={"description1"} className={classes.timer__time}>
        {time}
      </Typography>{" "}
      {isOnline && (
        <>
          <Typography
            fontSize={"md"}
            fontWeight={"semi-bold"}
            color={"primary-text"}
          >
            {t("live")}
          </Typography>
          <span className={clsx(classes.timer__dot)}>
            <l-ping size={"18"} color={color} speed={2} />
          </span>
        </>
      )}
      {!isOnline && (
        <>
          <Typography
            fontSize={"md"}
            fontWeight={"semi-bold"}
            color={"primary-text"}
          >
            {t("remaining")}
          </Typography>
          <span className={clsx(classes.timer__dot)}>
            <Icon name={"ClockCircle"} />
          </span>
        </>
      )}
    </div>
  );
});
